import { FunctionComponent, PropsWithChildren } from 'react';

import kebabCase from 'lodash/kebabCase';
import { useField } from 'react-final-form';

import { Button, Icons, Typography, Stack, tokens } from '@cast/design-system';

import { WorkloadOptimizationScalingPolicyFormModel } from 'features/cluster/workload-optimization/types';

import { PolicyInput } from '../PolicyInput';
import { useNullableCard } from './_hooks/useNullableCard';

import { useControlsGroupState } from '@cast/shared-rff-ui';

const DEFAULT_PODS_POLICY = { value: 4, periodSeconds: 60 };
const DEFAULT_PERCENT_POLICY = { value: 100, periodSeconds: 15 };

type PolicyCardProps = {
  typeLabel: string;
  name: string;
  addButtonText: string;
  defaultValue: { value: number; periodSeconds: number };
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  testIdPrefix?: string;
};

const PolicyCard = ({
  typeLabel,
  name,
  addButtonText,
  defaultValue,
  LabelComponent,
  testIdPrefix,
}: PolicyCardProps) => {
  const { readOnly, name: fieldName = '' } = useControlsGroupState({ name });
  const { input } = useField<WorkloadOptimizationScalingPolicyFormModel>(
    fieldName,
    { allowNull: true }
  );
  const { isAdded, handleAdd, handleRemove } = useNullableCard({
    input,
    defaultValue,
  });

  const Label = LabelComponent ?? Typography;

  if (!isAdded) {
    return (
      <Stack
        alignItems="flex-start"
        justifyContent="center"
        sx={{
          p: 16,
          borderRadius: '4px',
          backgroundColor: tokens.theme.bg.structural.surface,
          border: '1px solid',
          borderColor: tokens.theme.border.structural.neutralSubtle,
        }}
      >
        <Button
          variant="text"
          size="md"
          startIcon={<Icons.Plus />}
          disabled={readOnly}
          onClick={handleAdd}
          data-testid={testIdPrefix ? `add-${testIdPrefix}` : undefined}
        >
          {addButtonText}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      gap={8}
      sx={{
        px: 12,
        pt: 12,
        pb: 24,
        border: '1px solid',
        borderColor: tokens.theme.border.structural.neutralSubtle,
        borderRadius: '4px',
      }}
    >
      <Stack gap={4} sx={{ minWidth: 80 }}>
        <Label>
          <Typography
            variant="P10M"
            sx={{ color: tokens.theme.fg.structural.secondary }}
            component="span"
          >
            Type
          </Typography>
        </Label>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            height: 32,
            px: 8,
            border: '1px solid',
            borderColor: tokens.theme.border.structural.neutral,
            borderRadius: '4px',
            backgroundColor: tokens.theme.bg.structural.surface,
          }}
        >
          <Typography
            variant="P12B"
            color={
              readOnly
                ? tokens.theme.fg.structural.secondary
                : tokens.theme.fg.structural.primary
            }
          >
            {typeLabel}
          </Typography>
        </Stack>
      </Stack>
      <PolicyInput
        name={`${name}.value`}
        label="Limit"
        placeholder="1"
        variant="number"
        sx={{ flex: 1 }}
        LabelComponent={LabelComponent}
        testId={testIdPrefix ? `${testIdPrefix}-value` : undefined}
      />
      <PolicyInput
        name={`${name}.periodSeconds`}
        label="Period"
        placeholder="60"
        variant="sec"
        max={1800}
        sx={{ flex: 1 }}
        LabelComponent={LabelComponent}
        testId={testIdPrefix ? `${testIdPrefix}-period` : undefined}
      />
      <Button
        variant="ghost"
        size="sm"
        disabled={readOnly}
        onClick={handleRemove}
        sx={{ minWidth: 0, p: 4, mb: 2 }}
        data-testid={testIdPrefix ? `remove-${testIdPrefix}` : undefined}
      >
        <Icons.X size={16} />
      </Button>
    </Stack>
  );
};

type ScaleDirectionSectionProps = {
  title: string;
  directionName: 'scaleUp' | 'scaleDown';
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  stabilizationWindowTooltipContent: string;
};

const ScaleDirectionSection = ({
  title,
  directionName,
  LabelComponent,
  stabilizationWindowTooltipContent,
}: ScaleDirectionSectionProps) => {
  return (
    <Stack gap={16}>
      <Typography variant="h6">{title}</Typography>

      <PolicyInput
        name={`${directionName}.stabilizationWindowSeconds`}
        variant="sec"
        label="Stabilization window"
        placeholder="0"
        max={undefined}
        labelTooltip={stabilizationWindowTooltipContent}
        testId={`${kebabCase(directionName)}-stabilization-window`}
        LabelComponent={LabelComponent}
        sx={{ maxWidth: 121 }}
      />
      <Stack gap={8}>
        <PolicyCard
          name={`${directionName}.pods`}
          typeLabel="Pods"
          addButtonText="Add Pods"
          defaultValue={DEFAULT_PODS_POLICY}
          LabelComponent={LabelComponent}
          testIdPrefix={`${
            directionName === 'scaleUp' ? 'scale-up' : 'scale-down'
          }-pods`}
        />
        <PolicyCard
          name={`${directionName}.percent`}
          typeLabel="Percent"
          addButtonText="Add Percent"
          defaultValue={DEFAULT_PERCENT_POLICY}
          LabelComponent={LabelComponent}
          testIdPrefix={`${
            directionName === 'scaleUp' ? 'scale-up' : 'scale-down'
          }-percent`}
        />
      </Stack>
    </Stack>
  );
};

export const AutoscalerBehaviourField = ({
  LabelComponent,
}: {
  LabelComponent?: FunctionComponent<PropsWithChildren>;
}) => {
  return (
    <Stack gap={18}>
      <ScaleDirectionSection
        title="Scale up policies"
        directionName="scaleUp"
        LabelComponent={LabelComponent}
        stabilizationWindowTooltipContent="How long the autoscaler waits after a scale-up signal before adding replicas."
      />

      <ScaleDirectionSection
        title="Scale down policies"
        directionName="scaleDown"
        LabelComponent={LabelComponent}
        stabilizationWindowTooltipContent="How long the autoscaler waits after a scale-down signal before removing replicas. Kubernetes default is 300 seconds."
      />
    </Stack>
  );
};
